import { useEffect } from 'react'

import { useSearchParams } from 'react-router-dom'
import { useAppDispatch } from 'shared/config/store'
import { IFieldOption } from 'features/assets/model/types/assets'
import { SearchTagsType } from 'features/common/model/types/search'
import {
  fetchSearchResults,
  fetchSearchResultsByLabel,
  fetchSearchResultsByTagsOrStatuses,
  setIsSearchPage,
  setSearchAiTags,
  setSearchInputValue,
  setSearchTags,
  setSearchTagsType
} from 'features/common/model/slice/searchSlice'
import { SEARCH_PARAMS } from 'shared/utils/urlUtils'

interface UseSearchUrlParamsProps {
  systemFieldsByAssets?: any[]
  systemFieldsByBoards?: any[]
}

export const useSearchUrlParams = ({
  systemFieldsByAssets,
  systemFieldsByBoards
}: UseSearchUrlParamsProps) => {
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()

  const urlSearchTags = searchParams.get(SEARCH_PARAMS.SEARCH_TAGS)
  const urlSearchType = searchParams.get(SEARCH_PARAMS.SEARCH_TYPE)
  const urlSearchName = searchParams.get(SEARCH_PARAMS.SEARCH_NAME)
  const urlSearchAiTags = searchParams.get(SEARCH_PARAMS.SEARCH_AI_TAGS)
  const urlSearchStatus = searchParams.get(SEARCH_PARAMS.SEARCH_STATUS)

  useEffect(() => {
    const handleTagsSearch = () => {
      const searchQuery = JSON.parse(urlSearchTags as string)
      const searchType = JSON.parse(urlSearchType as string)
      if (
        Array.isArray(searchQuery) &&
        searchQuery.length > 0 &&
        searchType &&
        (systemFieldsByAssets?.length > 0 || systemFieldsByBoards?.length > 0)
      ) {
        const getTags = (fields: any[]) =>
          fields
            ?.find((field: any) => field.title === 'tags')
            ?.options.filter((option: IFieldOption) => searchQuery.includes(option.id)) || []

        const tagsByAssets = systemFieldsByAssets?.length > 0 ? getTags(systemFieldsByAssets) : []
        const tagsByBoards = systemFieldsByBoards?.length > 0 ? getTags(systemFieldsByBoards) : []

        // Merge and deduplicate tags by id
        const mergedTags = [...tagsByAssets, ...tagsByBoards]
        const uniqueTags = Array.from(new Map(mergedTags.map(tag => [tag.id, tag])).values())

        dispatch(setSearchTagsType(searchType))
        dispatch(setSearchTags(uniqueTags))
        dispatch(
          fetchSearchResultsByTagsOrStatuses({
            tagIds: searchQuery,
            searchTagsType: searchType,
            searchType: 'tags',
          })
        )
        dispatch(setIsSearchPage(true))
      }
    }

    const handleNameSearch = () => {
      const value = JSON.parse(urlSearchName as string)
      dispatch(fetchSearchResults({ value }))
      dispatch(setSearchInputValue(value))
      dispatch(setIsSearchPage(true))
    }

    const handleAiTagsSearch = () => {
      const label = JSON.parse(urlSearchAiTags as string)
      dispatch(fetchSearchResultsByLabel({ label }))
      dispatch(setSearchAiTags([{ name: label }]))
      dispatch(setIsSearchPage(true))
    }

    const handleStatusSearch = () => {
      const tagIds = JSON.parse(urlSearchStatus as string)
      dispatch(
        fetchSearchResultsByTagsOrStatuses({
          tagIds,
          searchTagsType: SearchTagsType.CONTAIN,
          searchType: 'status',
        })
      )
      dispatch(setIsSearchPage(true))
    }

    const handleResetSearch = () => {
      dispatch(setSearchTagsType(SearchTagsType.CONTAIN))
      dispatch(setSearchTags([]))
      dispatch(setIsSearchPage(false))
    }

    if (urlSearchTags && urlSearchType) {
      handleTagsSearch()
    } else if (urlSearchName) {
      handleNameSearch()
    } else if (urlSearchAiTags) {
      handleAiTagsSearch()
    } else if (urlSearchStatus) {
      handleStatusSearch()
    } else {
      handleResetSearch()
    }
  }, [
    dispatch,
    systemFieldsByAssets,
    systemFieldsByBoards,
    urlSearchTags,
    urlSearchType,
    urlSearchName,
    urlSearchAiTags,
    urlSearchStatus,
  ])
}
